import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type ContactMemberSchema, contactMemberSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ContactMemberForm } from "@/forms/contact-member-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface ProjectOverrideContext {
	projectId: string;
	contactId: string;
}

interface EditContactMemberModalProps extends ModalProps {
	contactId: string | null;
	contactMemberId: string;
	onComplete: () => Promise<void> | void;
	projectOverride?: ProjectOverrideContext;
}

export function EditContactMemberModal({
	isOpen,
	onClose,
	contactId,
	contactMemberId,
	onComplete,
	projectOverride,
}: EditContactMemberModalProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Context */
	const { defaultValues, loadFailed } = useGuardedDefaultValues<ContactMemberSchema>(async () => {
		const baseResponse = await client.contacts[":contactId"].members[":contactMemberId"].$get({
			param: { contactId: contactId || "unknown", contactMemberId },
		});
		const baseData = await responseHandler(baseResponse);
		if (!baseData.success) return {} as ContactMemberSchema;
		// The projection is as wide as the columns — a deleted type loads as null — and the
		// resolver makes the user close that gap before the form saves.
		if (!projectOverride) return baseData.contactMemberForm as ContactMemberSchema;

		const overrideResponse = await client.projects[":projectId"].contacts[":contactId"].members[
			":contactMemberId"
		].override.$get({
			param: {
				projectId: projectOverride.projectId,
				contactId: projectOverride.contactId,
				contactMemberId,
			},
		});
		const overrideData = await responseHandler(overrideResponse, false);
		return {
			...baseData.contactMemberForm,
			...((overrideData?.override?.overrides as object) ?? {}),
		} as ContactMemberSchema;
	});
	const methods = useForm({
		defaultValues,
		resolver: resolver(contactMemberSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: ContactMemberSchema) {
		if (projectOverride) {
			const response = await client.projects[":projectId"].contacts[":contactId"].members[
				":contactMemberId"
			].override.$patch({
				param: {
					projectId: projectOverride.projectId,
					contactId: projectOverride.contactId,
					contactMemberId,
				},
				json: json as unknown as Record<string, unknown>,
			});
			await responseHandler(response);
			handleOnClose();
			await onComplete();
			return;
		}
		const response = await client.contacts[":contactId"].members[":contactMemberId"].$patch({
			param: { contactId: contactId || "unknown", contactMemberId },
			json,
		});
		const data = await responseHandler(response);
		if (!data.success) return;
		handleOnClose();
		await onComplete();
	}

	function handleOnClose() {
		methods.reset();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={
					projectOverride ? t("contacts.edit_member_project_override") : t("contacts.edit_member")
				}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<ContactMemberForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
